import { memo, useEffect } from 'react'
import computeLineBetweenCircleUtil from '../../../../../core/utils/compute-line-between-circle.util'
import { AuthorMirrorAuthorInterface } from '../../../../../core/interface/report/author-mirror-author.interface'
import { ConstellationDataInterface } from '../../../../../core/interface/analytics/mirror/ConstellationDataInterface.interface'

const SvgAuthorGroup = ({
  svgContainer,
  data,
  edgeCenter = 0,
  handleDisplayAuthorCard,
  authorIsSelected,
  handleAuthorSelected,
}: {
  svgContainer: any
  data: ConstellationDataInterface<AuthorMirrorAuthorInterface>
  edgeCenter?: number
  handleDisplayAuthorCard: (
    author: ConstellationDataInterface<AuthorMirrorAuthorInterface> | null,
  ) => void
  authorIsSelected: boolean
  handleAuthorSelected: (author: AuthorMirrorAuthorInterface | null) => void
}) => {
  useEffect(() => {
    if (edgeCenter) {
      const coords = computeLineBetweenCircleUtil({
        x1: data.center.x,
        y1: data.center.y,
        r1: 50,
        x2: data.x,
        y2: data.y,
        r2: data.r,
      })
      const lineCenter = svgContainer
        .append('defs')
        .append('linearGradient')
        .attr('id', `line-center-${data.mirror.author_id}`)
        .attr('x1', data.center.x)
        .attr('x2', data.x)
        .attr('y1', data.center.y)
        .attr('y2', data.y)
        .attr('gradientUnits', 'userSpaceOnUse')
      lineCenter.append('stop').attr('stop-color', '#a4a4a4')

      lineCenter
        .append('stop')
        .attr('offset', 1)
        .attr('stop-color', '#a4a4a4')
        .attr('stop-opacity', 0)

      svgContainer
        .append('line')
        .attr('x1', coords.x1)
        .attr('y1', coords.y1)
        .attr('x2', coords.x2)
        .attr('y2', coords.y2)
        .attr('stroke', `url(#line-center-${data.mirror.author_id})`)
        .style('stroke-width', edgeCenter)
        .attr('class', `line-center`)
        .style('opacity', 0.5)
        .style('filter', 'blur(0.05px)')

      const lineCenter2 = svgContainer
        .append('defs')
        .append('linearGradient')
        .attr('id', `line-center-${data.mirror.author_id}`)
        .attr('x1', data.center.x)
        .attr('x2', data.x)
        .attr('y1', data.center.y)
        .attr('y2', data.y)
        .attr('gradientUnits', 'userSpaceOnUse')
      lineCenter2.append('stop').attr('stop-color', '#a4a4a4')

      lineCenter2
        .append('stop')
        .attr('offset', 1)
        .attr('stop-color', '#a4a4a4')
        .attr('stop-opacity', 0)

      lineCenter2
        .append('line')
        .attr('x1', coords.x1)
        .attr('y1', coords.y1)
        .attr('x2', coords.x2)
        .attr('y2', coords.y2)
        .attr('stroke', `url(#line-center-${data.mirror.author_id})`)
        .style('stroke-width', edgeCenter)
        .attr('class', `line-center2`)
        .style('opacity', 0.5)
        .style('filter', 'blur(5px)')
    }

    if (data?.edges?.length) {
      data?.edges?.forEach((edge: { x: any; y: any; r: any }) => {
        const coords = computeLineBetweenCircleUtil({
          x1: data.x,
          y1: data.y,
          r1: data.r,
          x2: edge.x,
          y2: edge.y,
          r2: edge.r,
        })
        svgContainer
          .append('line')
          .attr('class', `line-${data?.mirror?.author_id}`)
          .attr('x1', coords.x1)
          .attr('y1', coords.y1)
          .attr('x2', coords.x2)
          .attr('y2', coords.y2)
          .style('stroke', '#252525')
          .style('stroke-width', 1)
          .style('stroke-dasharray', '2 2')
          .style('opacity', 0)
      })
    }
    const group = svgContainer.append('g')
    group
      .attr('class', 'node-author')
      .attr('id', `node-author-${data?.mirror?.author_id}`)
    group
      .append('circle')
      .attr('cx', data.x)
      .attr('cy', data.y)
      .attr('r', data.r)
      .attr('fill', '#E3E3E3')

    const circleEffect = group
      .append('circle')
      .attr('cx', data.x)
      .attr('cy', data.y)
      .attr('r', data.r)
      .attr('fill', `url(#radialGradient-${data?.mirror?.author_id}-out`)

    const circleSelected = group
      .append('circle')
      .attr('cx', data.x)
      .attr('cy', data.y)
      .attr('r', data.r - 2)
      .attr('fill', `url(#radialGradient-${data?.mirror?.author_id}-in)`)
      .style('opacity', 0)

    if (authorIsSelected) {
      circleSelected.style('opacity', 1)
    } else {
      circleSelected.style('opacity', 0)
      circleEffect.attr(
        'fill',
        `url(#radialGradient-${data?.mirror?.author_id}-out`,
      )
    }

    group
      .on('mouseover', () => {
        if (authorIsSelected) {
          circleSelected.style('opacity', 1)
          circleEffect.style('opacity', 0)
        } else {
          circleSelected.style('opacity', 0)
          circleEffect.style('opacity', 1)
          circleEffect.attr(
            'fill',
            `url(#radialGradient-${data?.mirror?.author_id}-in)`,
          )
        }
        svgContainer?.selectAll(`[class="line-center"]`)?.style('opacity', 0)
        svgContainer
          ?.selectAll(`[class=line-${data.mirror.author_id}]`)
          ?.style('opacity', 0.3)
        svgContainer
          ?.selectAll(
            `[class="node-author"]:not(:is(${[
              `#node-author-${data?.mirror?.author_id}`,
              ...(data?.mirror?.edges || []).map(
                (edge: any) => `#node-author-${edge}`,
              ),
            ].join(',')}))`,
          )
          .style('opacity', 0.3)
        handleDisplayAuthorCard(data)
      })
      .on('mouseout', () => {
        handleDisplayAuthorCard(null)
        if (authorIsSelected) {
          circleSelected.style('opacity', 1)
          circleEffect.style('opacity', 0)
        } else {
          circleSelected.style('opacity', 0)
          circleEffect.style('opacity', 1)
          circleEffect.attr(
            'fill',
            `url(#radialGradient-${data?.mirror?.author_id}-out)`,
          )
        }
        svgContainer
          .selectAll(`[class="line-${data?.mirror?.author_id}"]`)
          .style('opacity', 0)
        svgContainer?.selectAll(`[class="line-center"]`)?.style('opacity', 1)
        svgContainer
          ?.selectAll(
            `[class="node-author"]:not([id="node-author-${data?.mirror?.author_id}"])`,
          )
          .style('opacity', 1)
      })
      .on('click', () => {
        if (authorIsSelected) {
          handleAuthorSelected(null)
        } else {
          handleAuthorSelected(data?.mirror)
        }
      })

    const a = group
      .append('text')
      .text(
        `${data?.mirror?.author?.name.slice(0, 35)}${
          data?.mirror?.author?.name?.length > 35 ? '...' : ''
        }`,
      )
      .attr('class', 'titleA')

    const w1 = a.node()?.getBBox().width || 0
    a.attr('x', data.x - w1 / 2).attr('y', data.y)
  }, [authorIsSelected])
  return null
}

export default memo(SvgAuthorGroup)
